import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Avatar } from '../common/Avatar';

/**
 * ProfileImage Component
 * 
 * Intelligently selects the optimal JPEG profile picture variant based on display size.
 * Uses pre-generated thumbnails for efficient loading.
 * 
 * Variants available:
 * - p64: 64x64 - For small thumbnails (notifications, mentions, compact lists)
 * - p128: 128x128 - For medium display (post headers, comments, sidebar)
 * - p256: 256x256 - For larger display (profile page header)
 * - original: Full size - For full-screen view
 * 
 * @example
 * // In post header (medium size)
 * <ProfileImage user={post.user} size="40px" />
 * 
 * // In notifications (small)
 * <ProfileImage user={user} size="32px" />
 * 
 * // In profile header (large)
 * <ProfileImage user={user} size="120px" />
 */
const ProfileImage = ({ 
  user, 
  size = '40px',
  showOnline = true,
  hideOnlineIndicator = false,
  indicatorPosition = 'right',
  isVerified,
  onClick,
  className,
  style,
  loading = 'lazy',
  fetchPriority = 'auto',
  ...props 
}) => {
  // Select the optimal variant based on requested size
  const imageSrc = useMemo(() => {
    if (!user) return null;
    
    // Get profilePictures map
    const variants = user.profilePictures;
    
    if (!variants || typeof variants !== 'object' || Object.keys(variants).length === 0) {
      return null; // No profile picture uploaded yet
    }
    
    // Parse size to get numeric value
    const sizeNum = parseInt(size) || 40;
    
    // Select variant based on size (pick variant that's >= requested size)
    // This ensures we don't upscale, but might slightly downscale
    if (sizeNum <= 64) {
      return variants.p64 || variants.p128 || variants.p256 || variants.original;
    } else if (sizeNum <= 128) {
      return variants.p128 || variants.p256 || variants.original;
    } else if (sizeNum <= 256) {
      return variants.p256 || variants.original;
    } else {
      return variants.original || variants.p256;
    }
  }, [user, size]);
  
  // Determine verification status
  const verified = isVerified !== undefined ? isVerified : user?.verified;
  
  return (
    <Avatar
      src={imageSrc}
      name={user?.fullName || user?.username}
      size={size}
      userId={user?.id}
      showOnline={showOnline}
      isOnline={user?.isOnline}
      hideOnlineIndicator={hideOnlineIndicator}
      indicatorPosition={indicatorPosition}
      isVerified={false} // Disable built-in badge to use custom logic
      onClick={onClick}
      className={className}
      style={style}
      loading={loading}
      fetchPriority={fetchPriority}
      {...props}
    />
  );
};

ProfileImage.propTypes = {
  /** User object containing profile picture data */
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fullName: PropTypes.string,
    username: PropTypes.string,
    verified: PropTypes.bool,
    isOnline: PropTypes.bool,
    /** JPEG variants object */
    profilePictures: PropTypes.shape({
      original: PropTypes.string,
      p256: PropTypes.string,
      p128: PropTypes.string,
      p64: PropTypes.string,
    }),
  }),
  /** Avatar size (e.g., '40px', '64px', '128px') */
  size: PropTypes.string,
  /** Show online/offline indicator */
  showOnline: PropTypes.bool,
  /** Force hide the online indicator */
  hideOnlineIndicator: PropTypes.bool,
  /** Position of online indicator */
  indicatorPosition: PropTypes.oneOf(['left', 'right']),
  /** Override verification badge display */
  isVerified: PropTypes.bool,
  /** Click handler */
  onClick: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
  /** Inline styles */
  style: PropTypes.object,
  /** Image loading strategy */
  loading: PropTypes.oneOf(['lazy', 'eager']),
  /** Fetch priority hint */
  fetchPriority: PropTypes.oneOf(['auto', 'high', 'low']),
};

export default ProfileImage;
